import { useEffect, useRef, useState } from 'react';
import { FiCheckCircle, FiCircle } from 'react-icons/fi';
import { FaSpinner } from 'react-icons/fa';
import { useTheme } from '../../../../contexts/theme-context';
import { OnboardingStepProps } from '../../registration';
import {
  Heading,
  SubHeading,
  ContentSection,
  PasswordFieldContainer,
  ContinueButton,
  ButtonsContainer,
  FieldGroup,
} from './secure-your-account.styled';
import { PasswordStrengthBar, calcScore } from '../../../../components/strength-bar/strength-bar';
import { FloatingInput } from '../../../../components/floating-input/floating-input';
import { ErrorText } from '../email-funnel/email-funnel.styled';
import { VSpace } from '../../../chat/settings/account/account-settings';
import { useAuth } from '../../../../contexts/auth/auth-context';
import { authApi } from '../../../../api';
import { extractErrorMessage } from '../../../../utils/error-utils';

export const SecureAccountStep: React.FC<OnboardingStepProps> = ({ goNext }) => {
  const { isLight } = useTheme();
  const { user, onboarding, setRegistration, syncAuthFromStorage } = useAuth();

  // const isDev = ENVIRONMENT === 'DEV';

  // Keep a resilient copy of the email for this step.
  // Source-of-truth: onboarding.email, fallback to auth user email if needed.
  const [userEmail, setUserEmail] = useState<string | null>(
    (onboarding?.email as string | undefined) ?? user?.email ?? null
  );

  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');

  const [triedContinue, setTriedContinue] = useState(false);
  const [fatalError, setFatalError] = useState<string | null>(null);

  // Local, reliable submit state (don't rely on external "loading")
  const [isSubmitting, setIsSubmitting] = useState(false);
  const clickedRef = useRef(false);

  // Ensure userEmail is populated even if context lost it (e.g., refresh).
  useEffect(() => {
    if (userEmail) return;

    const nextEmail =
      (onboarding?.email as string | undefined) ?? (user?.email as string | undefined);

    if (nextEmail) setUserEmail(nextEmail);
  }, [user?.email, onboarding?.email, userEmail]);

  // Skip if logged-in with Google
  useEffect(() => {
    if (user?.authProvider === 'google') {
      goNext();
    }
  }, [user?.authProvider]);

  // password rules
  const reqs = {
    length: pwd.length >= 8,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    number: /\d/.test(pwd),
    symbol: /[^A-Za-z0-9]/.test(pwd),
  };
  const checklistOk = Object.values(reqs).every(Boolean);

  const strengthScore = calcScore(pwd);
  const strongEnoughScore = strengthScore >= 10;

  // Keep both checks (you can relax to just `checklistOk` if desired)
  const isTooWeak = pwd.length > 0 && (!checklistOk || !strongEnoughScore);

  const passwordsMatch = pwd.length > 0 && pwd === confirm;

  // Do NOT permanently disable due to a past fatal error; allow retry after edits
  const isDisabled = isSubmitting || !userEmail || !passwordsMatch || isTooWeak;

  // Clear fatal error when user edits inputs to allow retry
  useEffect(() => {
    if (fatalError) setFatalError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwd, confirm]);

  /* save & advance */
  const handleContinue = async (e: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    setTriedContinue(true);

    if (isSubmitting || !userEmail || !passwordsMatch || isTooWeak) {
      return;
    }

    try {
      setIsSubmitting(true);
      clickedRef.current = true;

      // ✅ 1) update onboarding state with password (source of truth for onboarding flow)
      setRegistration({ password: pwd, authProvider: 'email' });

      /**
       * ONBOARDING SIGNUP:
       * Create account with email, password, and invitation code.
       * This calls POST /auth/signup-password which:
       * - Creates the Supabase user (or updates password if user exists from OTP)
       * - Sets up profile with invitation data
       * - Sets registration_step = 5 in the profile
       * - Marks invitation as used
       * - Sets up trial
       * - Returns session tokens (access_token, refresh_token)
       *
       * The API method also calls setSession() to store tokens in localStorage.
       */
      await authApi.signupPassword(userEmail, pwd, (onboarding as any)?.invitationCode);
      
      // Rehydrate auth state from stored tokens so that React state
      // (session, user) is set and the `authed` useMemo recalculates.
      // Without this, session/user stay null and authed stays false,
      // causing infinite redirect loops in AuthedGate after registration.
      await syncAuthFromStorage();

      goNext();
    } catch (e: any) {
      const msg = extractErrorMessage(e) || 'Unable to set password. Please try again or skip.';
      setFatalError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inactiveColor = isLight ? '#9AA7C7' : '#B7BDCBCC';

  /* ────────── UI ────────── */
  return (
    <ContentSection data-testid="ob-secure">
      <div data-testid="ob-secure-headings">
        <VSpace $h={61} />
        <Heading isLight={isLight} data-testid="ob-secure-heading">
          Secure your <br></br> experience.{' '}
        </Heading>
        <VSpace $h={16} />
        <SubHeading
          style={{ fontSize: '16px' }}
          isLight={isLight}
          data-testid="ob-secure-subheading"
        >
          Let’s lock down our private space.{' '}
        </SubHeading>
      </div>

      <div
        css={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 32 }}
        data-testid="ob-secure-body"
      >
        <PasswordFieldContainer data-testid="ob-secure-fields">
          <FieldGroup data-testid="ob-secure-fieldgroup">
            {/* CREATE PASSWORD */}
            <div style={{ position: 'relative' }} data-testid="ob-secure-create-wrap">
              <FloatingInput
                id="create-password"
                label="Create a password"
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                showToggle
                data-testid="ob-secure-pwd"
              />

              <div data-testid="ob-secure-strength">
                <PasswordStrengthBar score={strengthScore} allRequirementsMet={checklistOk} />
              </div>

              {/* checklist: always visible on mount */}
              <ul
                css={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '8px 0 0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  fontFamily: 'Inter, sans-serif',
                }}
                data-testid="ob-secure-checklist"
              >
                <Check ok={reqs.length} inactiveColor={inactiveColor}>
                  8&nbsp;+ characters
                </Check>
                <Check ok={reqs.upper} inactiveColor={inactiveColor}>
                  Upper-case letter
                </Check>
                <Check ok={reqs.lower} inactiveColor={inactiveColor}>
                  Lower-case letter
                </Check>
                <Check ok={reqs.number} inactiveColor={inactiveColor}>
                  Number
                </Check>
                <Check ok={reqs.symbol} inactiveColor={inactiveColor}>
                  Symbol&nbsp;(!@#$…)
                </Check>
              </ul>
            </div>

            {/* CONFIRM PASSWORD */}
            <div style={{ position: 'relative' }} data-testid="ob-secure-confirm-wrap">
              <FloatingInput
                id="confirm-password"
                label="Confirm password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                showToggle
                data-testid="ob-secure-confirm"
              />
            </div>
          </FieldGroup>

          {/* weak message */}
          {triedContinue && isTooWeak && (
            <div css={{ paddingTop: 8 }} data-testid="ob-secure-weak">
              <ErrorText>
                Password too weak. Add upper/lower case letters, numbers and symbols.
              </ErrorText>
            </div>
          )}

          {/* fatal session error */}
          {fatalError && (
            <div css={{ paddingTop: 8 }} data-testid="ob-secure-fatal">
              <ErrorText>{fatalError}</ErrorText>
            </div>
          )}
        </PasswordFieldContainer>
      </div>
      <ButtonsContainer data-testid="ob-secure-buttons">
        <ContinueButton
          type="button"
          disabled={isDisabled}
          onClick={handleContinue}
          data-testid="ob-secure-continue"
        >
          {isSubmitting ? <FaSpinner data-testid="ob-secure-spinner" style={{ animation: 'spin 1s linear infinite' }} /> : 'Continue'}
        </ContinueButton>
      </ButtonsContainer>
    </ContentSection>
  );
};

(SecureAccountStep as any).background = 'linear-gradient(180deg, #222327 0%, #1A1A1C 100%);';
(SecureAccountStep as any).progress = false;
(SecureAccountStep as any).smallLogo = true;

const Check: React.FC<{ ok: boolean; inactiveColor?: string; children: React.ReactNode }> = ({
  ok,
  inactiveColor = '#B7BDCBCC',
  children,
}) => (
  <li
    css={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      paddingTop: 4,
      paddingLeft: 17,
    }}
  >
    <span
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#8097EA',
        lineHeight: 0,
      }}
    >
      {ok ? <FiCheckCircle size={14} /> : <FiCircle size={14} />}
    </span>
    <span
      css={{
        fontFamily: 'Inter, sans-serif',
        fontSize: 12,
        fontWeight: ok ? 600 : 400,
        color: ok ? '#8097EA' : inactiveColor,
      }}
    >
      {children}
    </span>
  </li>
);
